import { ArraySourceComposer } from '@/auth/api/connector'
import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { azienda, offerta, sede } from '@/model/qsadminapi/QsAdminApiModuleModel'
import {
  ButtonItem,
  ButtonOptions,
  Form as DXForm,
  EmptyItem,
  GroupItem,
  RequiredRule,
  SimpleItem,
  Tab,
  TabbedItem,
  TabPanelOptions,
  FormRef,
} from 'devextreme-react/form'
import DataSource from 'devextreme/data/data_source'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useParams, useRevalidator, useSearchParams } from 'react-router-dom'
import { FieldDataChangedEvent } from 'devextreme/ui/form'
import { ProjectEditorFormProps } from '@/routes/projects/ProjectEditor.types'
import LoadingPanel from '@/components/loading-panel/LoadingPanel'
import notify from 'devextreme/ui/notify'
import { PopupRef } from 'devextreme-react/cjs/popup'
import FormPopupClientNote from '@/components/form-popup-client-note/FormPopupClientNote'
import {
  customButton,
  isUserRoleAllowed,
  onInitializedHtmlEditor,
  onValueChangedHtmlEditor,
  toolbarHtmlEditor,
} from '@/routes/utils'
import { Roles } from '@/auth/azure/Roles'
import { ValueChangedEvent as ValueChangedEventSelectBox } from 'devextreme/ui/select_box'
import { useScreenSize } from '@/themes/media-query'
import { ClickEvent } from 'devextreme/ui/button'
import { DropDownButton } from 'devextreme-react'
import ProjectEditorFormActivitiesGridMemoed from '@/routes/projects/ProjectEditor.activities'
import BriefAnalysisGridMemoed from '@/components/brief-grids/BriefAnalysisGrid'
import ProjectEditorFormOffersGridMemoed from '@/routes/projects/ProjectEditor.offers'
import QuotesBriefGridMemoed from '@/routes/projects/QuotesBriefGrid'
import ProjectEditorFormOrdersGridMemoed from '@/routes/projects/ProjectEditor.orders'
import ProjectEditorFormTasksGridMemoed from '@/routes/projects/ProjectEditor.tasks'
import { StatoCommessa } from '@/routes/projects/ProjectEditor.enums'
import { ODataStore } from 'devextreme/common/data'
import { ODataStoreRequestConfiguration } from '@/auth/api/config'
import { useTokenRefresh } from '@/auth/azure/azureManager'

const ProjectEditorForm = (props: ProjectEditorFormProps) => {
  const {
    creating,
    project,
    stati,
    aziende,
    impiegati,
    isRoleAllowed,
    userInfo,
    fetcher,
    canCreateRelatedObjects = isRoleAllowed,
  } = props

  const { clientId } = useParams()
  const [searchParams] = useSearchParams()
  const activityId = searchParams.get('activityId')
  const navigate = useNavigate()
  const qsAdminApi = useQsAdminApiManager.getState().service
  const formRef = useRef<FormRef>(null)
  const revalidator = useRevalidator()
  const [currentSite, setCurrentSite] = useState<sede | null | undefined>()
  const popupNoteClienteRef = useRef<PopupRef>(null)
  const currentScreenSize = useScreenSize()
  const token = useTokenRefresh()

  const [currentCompany, setCurrentCompany] = useState<azienda | null | undefined>(
    clientId ? (aziende ? aziende[0] : undefined) : project && 'sede' in project ? project?.sede?.azienda : undefined,
  )
  const [currentCompanyChanged, setCurrentCompanyChanged] = useState<boolean>(clientId && creating ? true : false)
  const [sediDataSource, setSediDataSource] = useState<DataSource>(
    ArraySourceComposer('id', project && 'sede' in project ? project?.sede?.azienda?.sedi : undefined),
  )

  const [loadPanelVisible, setLoadPanelVisible] = useState(false)

  const getCompanySites = useCallback(
    async (companyId: number) => {
      const sitesData = await qsAdminApi.sede().query((builder, sede) => {
        builder.filter(sede.azienda.props.id.eq(companyId))
        builder.orderBy(sede.principale.desc())
        builder.expanding('azienda', (aziendaSedeBuilder, azienda) => {
          aziendaSedeBuilder.expanding('sedi', (sediAziendaSedeBuilder, sedeAzienda) => {
            sediAziendaSedeBuilder.select('id', 'nome', 'note')
            sediAziendaSedeBuilder.orderBy(sedeAzienda.principale.desc())
          })
          aziendaSedeBuilder.select('id', 'nome', 'sedi')
        })
        builder.select('id', 'nome', 'azienda', 'note')
      })
      return sitesData.data.value
    },
    [qsAdminApi],
  )

  useEffect(() => {
    if (!currentCompanyChanged) return
    console.log('COMPANY EFFECT', currentCompany)
    if (currentCompany) {
      getCompanySites(currentCompany?.id).then((sites: sede[]) => {
        console.log('SITES', sites)
        setSediDataSource(ArraySourceComposer('id', sites))
        formRef.current?.instance().updateData('sede', sites[0])
      })
      setCurrentCompanyChanged(false)
    }
  }, [currentCompanyChanged, currentCompany, getCompanySites])

  return (
    <>
      <LoadingPanel visible={!(fetcher?.state === 'idle' && revalidator.state === 'idle' && !loadPanelVisible)} />
      <DXForm
        id={'project_form'}
        formData={project}
        labelLocation={'top'}
        showValidationSummary={true}
        onFieldDataChanged={(e: FieldDataChangedEvent) => {
          if (
            formRef.current?.instance().option().isDirty !== null &&
            formRef.current?.instance().option().isDirty !== undefined
          ) {
            console.log('is dirty', formRef.current?.instance().option().isDirty)
            formRef.current
              .instance()
              .getButton('btn-save')
              ?.option('disabled', !formRef.current?.instance().option().isDirty)
            formRef.current
              .instance()
              .getButton('btn-cancel')
              ?.option('disabled', !formRef.current?.instance().option().isDirty)
          }
        }}
        validationGroup="projectValidation"
        ref={formRef}
        readOnly={!isRoleAllowed}
      >
        <GroupItem colCountByScreen={{ xs: 1, sm: 2, md: 2, lg: 2 }}>
          <GroupItem colCountByScreen={{ xs: 1, sm: 1, md: 1, lg: 2 }}>
            <SimpleItem
              label={{ text: 'Azienda' }}
              editorType="dxSelectBox"
              editorOptions={{
                buttons: [
                  customButton('open', (e: ClickEvent) => {
                    if (currentCompany) window.open(`/clients/${currentCompany.id}`, '_blank')
                    else notify(`Deve essere selezionata un' azienda per poterla aprire.`, 'warning', 3000)
                  }),
                  { name: 'dropDown', location: 'after' },
                ],
                value: currentCompany,
                dataSource: ArraySourceComposer('id', aziende),
                placeholder: "Seleziona l'azienda...",
                displayExpr: 'nome',
                searchEnabled: true,
                readOnly: !isRoleAllowed || clientId || activityId,
                dropDownOptions: {
                  showTitle: true,
                  title: "Selezionare l'azienda",
                  hideOnOutsideClick: true,
                },
                onValueChanged: (e: ValueChangedEventSelectBox) => {
                  if (e.previousValue?.id === e.value?.id) return
                  console.log('COMPANY EVENT CHANGED', e.previousValue, e.value)
                  setCurrentCompany(e.value)
                  setCurrentCompanyChanged(true)
                },
              }}
            >
              <RequiredRule message="Azienda obbligatoria"></RequiredRule>
            </SimpleItem>
            <SimpleItem
              dataField="sede"
              label={{ text: 'Sede' }}
              editorType="dxSelectBox"
              editorOptions={{
                buttons: [
                  customButton(
                    'info',
                    () => {
                      if (project.sede) {
                        setCurrentSite(project.sede)
                        popupNoteClienteRef.current?.instance().show()
                      } else notify('Deve essere selezionata una sede per aprire le note.', 'warning', 3000)
                    },
                    false,
                    'Note cliente',
                  ),
                  {
                    name: 'clear',
                    location: 'after',
                  },
                  { name: 'dropDown', location: 'after' },
                ],
                dataSource: sediDataSource,
                placeholder: 'Seleziona la sede...',
                displayExpr: 'nome',
                searchEnabled: true,
                showDropDownButton: true,
                dropDownButtonTemplate: 'dropDownButton',
                dropDownOptions: {
                  showTitle: true,
                  title: 'Selezionare la sede',
                  hideOnOutsideClick: true,
                },
                readOnly: !isRoleAllowed || (activityId !== null && activityId !== undefined),
              }}
            >
              <RequiredRule message="Sede obbligatoria"></RequiredRule>
            </SimpleItem>
            <SimpleItem
              dataField="autore"
              label={{ text: 'Autore' }}
              editorType="dxLookup"
              editorOptions={{
                dataSource: ArraySourceComposer('id', impiegati),
                placeholder: 'Seleziona autore...',
                displayExpr: 'fullname',
                searchEnabled: true,
                dropDownCentered: true,
                readOnly: true,
                dropDownOptions: {
                  showTitle: true,
                  title: 'Selezionare autore',
                  hideOnOutsideClick: true,
                },
              }}
            >
              <RequiredRule message="Autore obbligatorio" />
            </SimpleItem>
            <SimpleItem
              dataField="creazione"
              editorOptions={{
                displayFormat: 'dd/MM/yyyy',
                readOnly: true,
              }}
              editorType="dxDateBox"
              label={{ text: 'Data Creazione' }}
            >
              <RequiredRule message="Data creazione obbligatoria" />
            </SimpleItem>
            <SimpleItem
              dataField="inizio"
              editorOptions={{
                displayFormat: 'dd/MM/yyyy',
                type: 'date',
                pickerType: 'calendar',
                applyValueMode: 'instantly',
              }}
              editorType="dxDateBox"
              label={{ text: 'Data inizio' }}
            >
              <RequiredRule message="Data inizio obbligatoria"></RequiredRule>
            </SimpleItem>
            <SimpleItem
              dataField="fine"
              editorOptions={{
                displayFormat: 'dd/MM/yyyy',
                type: 'date',
                pickerType: 'calendar',
                applyValueMode: 'instantly',
                showClearButton: true,
              }}
              editorType="dxDateBox"
              label={{ text: 'Data fine' }}
            ></SimpleItem>
            <SimpleItem
              dataField="stato"
              label={{ text: 'Stato' }}
              editorType="dxLookup"
              editorOptions={{
                dataSource: ArraySourceComposer('id', stati),
                placeholder: 'Seleziona lo stato...',
                displayExpr: 'nome',
                searchEnabled: true,
                dropDownCentered: true,
                dropDownOptions: {
                  showTitle: true,
                  title: 'Selezionare lo stato',
                  hideOnOutsideClick: true,
                },
                readOnly: !isRoleAllowed || creating,
              }}
            >
              <RequiredRule message="Stato obbligatorio"></RequiredRule>
            </SimpleItem>
            <EmptyItem></EmptyItem>
            <SimpleItem colSpan={2} dataField={'titolo'} editorType={'dxTextArea'}>
              <RequiredRule message="Titolo obbligatorio" />
            </SimpleItem>
          </GroupItem>
          <GroupItem colCount={1}>
            <TabbedItem>
              <TabPanelOptions
                animationEnabled={true}
                swipeEnabled={currentScreenSize.isSmall || currentScreenSize.isXSmall}
                deferRendering={false}
                scrollingEnabled={true}
              />
              <Tab title="Note">
                <SimpleItem
                  dataField="note"
                  label={{ visible: false }}
                  editorType="dxHtmlEditor"
                  editorOptions={{
                    stylingMode: 'filled',
                    valueType: 'html',
                    toolbar: toolbarHtmlEditor(currentScreenSize),
                    mediaResizing: {
                      enabled: true,
                    },
                    imageUpload: {
                      tabs: ['file', 'url'],
                      fileUploadMode: 'base64',
                    },
                    height: 325,
                    onValueChanged: onValueChangedHtmlEditor,
                    onInitialized: onInitializedHtmlEditor,
                  }}
                >
                  <RequiredRule message="Note obbligatorie"></RequiredRule>
                </SimpleItem>
              </Tab>
              {isUserRoleAllowed(userInfo?.roles, [
                Roles.GlobalAdministrator,
                Roles.Administrator,
                Roles.Sales,
                Roles.ExternalSales,
                Roles.Marketing,
                Roles.Supervisor,
              ]) && (
                <Tab title="Attività Commerciali">
                  <ProjectEditorFormActivitiesGridMemoed
                    project={project}
                    userInfo={userInfo}
                  ></ProjectEditorFormActivitiesGridMemoed>
                </Tab>
              )}
              {isUserRoleAllowed(userInfo?.roles, [
                Roles.GlobalAdministrator,
                Roles.Administrator,
                Roles.Sales,
                Roles.ExternalSales,
                Roles.Marketing,
                Roles.TechDeveloper,
                Roles.Supervisor,
              ]) && (
                <Tab title="Analisi">
                  <BriefAnalysisGridMemoed
                    dataSource={
                      new DataSource({
                        store: new ODataStore({
                          url: `${import.meta.env.VITE_QSADMINAPI_HOST}/offerta`,
                          key: 'id',
                          version: 4,
                          errorHandler: (e) => {
                            console.error(e.errorDetails)
                            if (!e.errorDetails) return
                            notify(
                              {
                                message: `Errore : ${e.errorDetails?.message}`,
                                type: 'error',
                                displayTime: 5000,
                              },
                              {
                                position: 'bottom center',
                                direction: 'up-push',
                              },
                            )
                          },
                          beforeSend: ODataStoreRequestConfiguration(token),
                          deserializeDates: false,
                        }),
                        postProcess: (result: offerta[]) => {
                          console.log('result', result)
                          return result.filter(
                            (offerta: offerta) => offerta.analisi !== null && offerta.analisi !== undefined,
                          )
                        },
                        filter: [['commessa.id', project.id]],
                        expand: ['analisi($expand=riferimenti,riferimenti_entita,stato,creatore)'],
                      })
                    }
                    userInfo={userInfo}
                    isNestedData={true}
                    storageKey="project-analysis-datagrid"
                  ></BriefAnalysisGridMemoed>
                </Tab>
              )}
              {isUserRoleAllowed(userInfo?.roles, [
                Roles.GlobalAdministrator,
                Roles.Administrator,
                Roles.Sales,
                Roles.ExternalSales,
                Roles.Marketing,
                Roles.Supervisor,
              ]) && (
                <Tab title="Offerte">
                  <ProjectEditorFormOffersGridMemoed
                    project={project}
                    userInfo={userInfo}
                  ></ProjectEditorFormOffersGridMemoed>
                </Tab>
              )}
              {isUserRoleAllowed(userInfo?.roles, [
                Roles.GlobalAdministrator,
                Roles.Administrator,
                Roles.Sales,
                Roles.ExternalSales,
                Roles.Marketing,
                Roles.Supervisor,
              ]) && (
                <Tab title="Quote">
                  <QuotesBriefGridMemoed project={project}></QuotesBriefGridMemoed>
                </Tab>
              )}
              {isUserRoleAllowed(userInfo?.roles, [Roles.Guest]) && (
                <Tab title="Ordini">
                  <ProjectEditorFormOrdersGridMemoed
                    project={project}
                    userInfo={userInfo}
                  ></ProjectEditorFormOrdersGridMemoed>
                </Tab>
              )}
              {isUserRoleAllowed(userInfo?.roles, [Roles.Guest]) && (
                <Tab title="Task">
                  <ProjectEditorFormTasksGridMemoed
                    project={project}
                    userInfo={userInfo}
                  ></ProjectEditorFormTasksGridMemoed>
                </Tab>
              )}
            </TabbedItem>
          </GroupItem>
        </GroupItem>
        <GroupItem visible={isRoleAllowed} cssClass="last-group">
          <GroupItem cssClass="buttons-group" colCount={creating || !canCreateRelatedObjects ? 2 : 3}>
            {canCreateRelatedObjects && (
              <SimpleItem>
                <DropDownButton
                  id="ddbAzioni-project"
                  text="Crea"
                  disabled={creating}
                  useItemTextAsTitle={false}
                  items={[
                    {
                      icon: 'bookmark',
                      text: 'Attività commerciale',
                      onClick: async (e: ClickEvent) => {
                        if (project?.stato?.id === StatoCommessa.APERTA)
                          if (clientId)
                            navigate({
                              pathname: `/clients/${clientId}/activities/new`,
                              search: `?projectId=${project.id}`,
                            })
                          else
                            navigate({
                              pathname: '/activities/new',
                              search: `?projectId=${project.id}`,
                            })
                        else
                          notify(
                            `La commessa deve essere nello stato 'APERTA'. Stato: '${project.stato?.nome}'`,
                            'warning',
                            3000,
                          )
                      },
                    },
                    {
                      icon: 'money',
                      text: 'Offerta',
                      onClick: async (e: ClickEvent) => {
                        if (project?.stato?.id === StatoCommessa.APERTA)
                          if (clientId)
                            navigate({
                              pathname: `/clients/${clientId}/offers/new`,
                              search: `?projectId=${project.id}`,
                            })
                          else
                            navigate({
                              pathname: `/offers/new`,
                              search: `?projectId=${project.id}`,
                            })
                        else
                          notify(
                            `La commessa deve essere nello stato 'APERTA'. Stato: '${project.stato?.nome}'`,
                            'warning',
                            3000,
                          )
                      },
                    },
                    {
                      icon: 'money',
                      text: 'Quota',
                      onClick: async (e: ClickEvent) => {
                        if (project?.stato?.id === StatoCommessa.APERTA)
                          if (clientId)
                            navigate({
                              pathname: `/clients/${clientId}/quotes/new`,
                              search: `?projectId=${project.id}`,
                            })
                          else
                            navigate({
                              pathname: '/quotes/new',
                              search: `?projectId=${project.id}`,
                            })
                        else
                          notify(
                            `La commessa deve essere nello stato 'APERTA'. Stato: '${project.stato?.nome}'`,
                            'warning',
                            3000,
                          )
                      },
                    },
                  ]}
                  dropDownOptions={{ width: '250' }}
                  width={'150px'}
                />
              </SimpleItem>
            )}
            <ButtonItem name="btn-save">
              <ButtonOptions
                disabled={!formRef.current?.instance().option().isDirty}
                icon="save"
                text="Salva"
                width="150px"
                onClick={async () => {
                  const validationResult = formRef.current?.instance().validate()
                  if (!validationResult?.isValid) return
                  console.log('validation ok')
                  console.log('project', project)
                  if (project) {
                    fetcher?.submit(JSON.stringify(project), {
                      method: creating ? 'POST' : 'PUT',
                      encType: 'application/json',
                    })
                  } else console.error('no data tu submit')
                }}
              />
            </ButtonItem>
            {!creating && (
              <ButtonItem name="btn-cancel">
                <ButtonOptions
                  disabled={!formRef.current?.instance().option().isDirty}
                  width="150px"
                  text="Annulla"
                  icon="undo"
                  onClick={() => {
                    revalidator.revalidate()
                  }}
                />
              </ButtonItem>
            )}
          </GroupItem>
        </GroupItem>
      </DXForm>
      <FormPopupClientNote
        data={currentSite}
        refPopup={popupNoteClienteRef}
        refreshDataSourceSites={() => {
          if (currentCompany) {
            getCompanySites(currentCompany?.id).then((sites: sede[]) => {
              console.log('SITES', sites)
              setSediDataSource(ArraySourceComposer('id', sites))
              formRef.current?.instance().updateData(
                'sede',
                sites.find((site: sede) => {
                  return site.id === currentSite?.id
                }),
              )
            })
          }
        }}
        isRoleAllowed={isUserRoleAllowed(userInfo?.roles, [
          Roles.TechDeveloper,
          Roles.Sales,
          Roles.GlobalAdministrator,
          Roles.ExternalSales,
          Roles.Administrator,
        ])}
      ></FormPopupClientNote>
    </>
  )
}

export default ProjectEditorForm
